import { lazy, Suspense, useEffect, useId, useRef, useState } from 'react'
import { LazyMotion } from 'framer-motion'
import { ArrowDown } from '@phosphor-icons/react/dist/ssr/ArrowDown'
import { ArrowUpRight } from '@phosphor-icons/react/dist/ssr/ArrowUpRight'
import { Check } from '@phosphor-icons/react/dist/ssr/Check'
import { CopySimple } from '@phosphor-icons/react/dist/ssr/CopySimple'
import { FileText } from '@phosphor-icons/react/dist/ssr/FileText'
import { Handshake } from '@phosphor-icons/react/dist/ssr/Handshake'
import { Question } from '@phosphor-icons/react/dist/ssr/Question'
import { ShareNetwork } from '@phosphor-icons/react/dist/ssr/ShareNetwork'
import { ShieldCheck } from '@phosphor-icons/react/dist/ssr/ShieldCheck'
import { Timer } from '@phosphor-icons/react/dist/ssr/Timer'
import { UserFocus } from '@phosphor-icons/react/dist/ssr/UserFocus'
import { CaseStudyBookCallCta } from '@/components/case-study/CaseStudyBookCallCta'
import { CaseStudyContentSheet } from '@/components/case-study/CaseStudyContentSheet'
import type { TocItem } from '@/components/case-study/caseStudyDefaultToc'
import { HomeAiCaseStudies } from '@/components/home/HomeAiCaseStudies'
import { SeoHead } from '@/components/seo/SeoHead'
import { getAllCaseStudies, getAllNotes } from '@/data/contentService'
import type { CaseStudy, Note } from '@/domain/content'
import { copyToClipboard } from '@/lib/copyToClipboard'
import wildflowerFieldPoster from '@/assets/wildflower-field-poster.avif'

const pathname = '/custom-web'
const heroVideoUrl = 'https://assets.gosha.ee/bgs/abstract-glass-ribbon_web.mp4'
const examplesVideoUrl = 'https://assets.gosha.ee/bgs/wildflower-field_web.mp4'
const unitedExampleVideoUrl =
  'https://assets.gosha.ee/bgs/hf_20260518_153848_cea6b341-a5df-44c1-8aed-3935da64cdf0.mp4'
const loadDomAnimation = () =>
  import('@/lib/motion-dom-animation').then((mod) => mod.domAnimation)
const HomeTestimonials = lazy(() =>
  import('@/components/home/HomeTestimonials').then((mod) => ({ default: mod.HomeTestimonials }))
)

const readAlsoSlugs = [
  'united-accountants-one-to-one-follow-up-pages',
  'united-accountants-custom-landing-pages-gtm-system',
  'personalized-outreach-visuals-workflow',
  'automated-outbound-outreach-workflow',
]

const readAlsoNoteSlugs = [
  'broken-handoff-between-sales-and-website',
  'customers-want-the-task-gone-outcomes-over-saas',
]

const introCopy = `Привет. Хочу познакомить тебя с Гошей.

Он работает с B2B sales-командами и помогает им делать гиперперсональные follow-up страницы после звонков с клиентами.

Идея в том, чтобы sales мог в один клик собрать страницу под конкретного клиента: что обсудили, почему это важно, какие есть примеры и что делать дальше.

Те, с кем он это уже тестировал, говорят, что клиентам становится проще продавать идею внутри своей компании. Лучше держится фокус, больше прозрачности, меньше потери контекста после звонка.

Сейчас он ищет единомышленников, founders, sales/GTM людей и команды, с которыми можно поговорить на эту тему. Думаю, вам стоит познакомиться.

Коротко здесь: [link]`

const contentSheetItems: TocItem[] = [
  { label: 'Зачем', anchor: '#why' },
  { label: 'Что получается', anchor: '#outputs' },
  { label: 'Примеры', anchor: '#examples' },
  { label: 'Интро', anchor: '#intro' },
  { label: 'FAQ', anchor: '#faq' },
]

const whyCards = [
  {
    title: 'Гиперперсонально под клиента',
    body: 'Материал собирается вокруг конкретной компании, разговора, вопросов, рисков и следующего шага.',
    Icon: UserFocus,
  },
  {
    title: 'Очень легко для оператора',
    body: 'Sales не пишет все вручную. Он берет контекст звонка и быстро получает готовую страницу.',
    Icon: Timer,
  },
  {
    title: 'Помогает двигать сделку',
    body: 'Клиенту проще переслать контекст внутри своей команды, продолжить диалог и вернуться к договоренностям.',
    Icon: ShareNetwork,
  },
]

const outputs = [
  {
    title: 'Страница после звонка',
    body: 'Что обсудили, что важно, какие вопросы остались и какой следующий шаг.',
    Icon: FileText,
  },
  {
    title: 'Страница для внутренней пересылки',
    body: 'Чтобы человеку было проще показать идею CEO, команде или другому участнику решения.',
    Icon: ShareNetwork,
  },
  {
    title: 'Страница с примерами',
    body: 'Несколько релевантных кейсов или доказательств, собранных под конкретную ситуацию.',
    Icon: ShieldCheck,
  },
  {
    title: 'Страница под конкретную компанию',
    body: 'Контекст аккаунта, его задача, подходящий угол и аккуратное предложение продолжить разговор.',
    Icon: UserFocus,
  },
  {
    title: 'Расчет или business case',
    body: 'Когда нужно объяснить экономику, риски, варианты и недостающие вводные.',
    Icon: Timer,
  },
  {
    title: 'Калькулятор',
    body: 'Когда человеку нужно самому поиграть с вводными: объем, стоимость, сроки, экономия или сценарии.',
    Icon: Question,
  },
  {
    title: 'Ценовое предложение',
    body: 'Когда цену лучше показать вместе с логикой: что входит, какие есть варианты и почему это так собрано.',
    Icon: Handshake,
  },
  {
    title: 'Ответ на повторяющийся вопрос',
    body: 'Если разные клиенты спрашивают одно и то же: безопасность, процесс, сроки, цена или интеграции.',
    Icon: Question,
  },
]

const examples = [
  {
    tag: 'Следующий шаг',
    title: 'закрепляем договоренность',
    label: 'gosha.ee',
    href: 'https://gosha.ee/hello/mira-voss-604218/',
    logo: '/favicon.svg',
    logoInset: false,
    video: examplesVideoUrl,
    poster: wildflowerFieldPoster,
  },
  {
    tag: 'После разговора',
    title: 'рекап первого диалога',
    label: 'United Accountants',
    href: 'https://www.unitedaccountants.com/hello/EXAMPLE-northstar-ledgerworks-4v9q2p',
    logo: '/clients/united-accountants-icon.avif',
    logoInset: false,
    video: unitedExampleVideoUrl,
    poster: undefined,
  },
]

const faqItems = [
  {
    question: 'Как это работает?',
    answer:
      'Сначала собираем контекст предложения: что компания продает, кому, какие вопросы часто возникают и почему это сложно объяснить обычным сайтом или письмом. После этого я даю правила для входных данных, собираю автоматизацию, тестирую ее на реальных сценариях и передаю sales. Дальше sales может сам делать новый asset за минуты.',
  },
  {
    question: 'Что нужно от команды?',
    answer:
      'Нужно один раз разобраться в предложении, аудитории, частых вопросах, примерах и типовых следующих шагах. Потом для каждой новой страницы нужно загрузить запись звонка в AI, и все.',
  },
  {
    question: 'Почему не обычное письмо?',
    answer:
      'Рядовое письмо легко пропустить, особенно в длинной сделке, где после звонка нужно обсудить идею с другими людьми. Страница лучше удерживает внимание: она собирает контекст, вопросы, релевантные примеры, расчеты и следующий шаг в одном месте. Ее проще переслать внутри команды, чем длинную переписку.',
  },
  {
    question: 'Почему не классический сайт?',
    answer:
      'Классический сайт объясняет продукт всем одинаково и часто начинает разговор с нуля. После звонка клиенту нужен не общий pitch, а материал вокруг своей ситуации: что уже обсудили, почему это важно именно для него, какие есть доказательства и что делать дальше. Это не замена сайту, а продолжение конкретного разговора.',
  },
  {
    question: 'Кому это может быть полезно?',
    answer:
      'Founder, sales/GTM, tech business, agencies и consultants, которые продают сложный B2B-продукт или сервис через длинные разговоры. Лучше всего подходит там, где решение дорогое, в покупке участвует несколько людей, а champion должен объяснять идею внутри своей команды. Особенно GovTech, cybersec, infrastructure, AI, devtools и похожие сферы, где важно снижать риск и сохранять контекст.',
  },
]

const sectionTitleClass =
  "ui-heading-balance font-['Manrope'] text-[26px] font-normal leading-[1.08] tracking-normal text-[var(--foreground)]"

const cardClass = 'rounded-[10px] border border-[var(--border)] bg-white p-4'

const SectionHeader = ({ eyebrow, title }: { eyebrow: string; title: string }) => (
  <div className="space-y-1">
    <p className="text-[11px] font-semibold uppercase leading-4 tracking-[0.14em] text-[var(--foreground-subtle)]">
      {eyebrow}
    </p>
    <h2 className={sectionTitleClass}>{title}</h2>
  </div>
)

const CopyIcon = ({ copied }: { copied: boolean }) =>
  copied ? (
    <Check size={17} weight="bold" className="ml-1 inline-block align-[-2px] text-[var(--foreground)]" aria-hidden />
  ) : (
    <CopySimple size={17} weight="regular" className="ml-1 inline-block align-[-2px] text-[var(--foreground)]" aria-hidden />
  )

const FaqList = () => {
  const baseId = useId()
  const [openIndex, setOpenIndex] = useState(-1)

  return (
    <div className="w-full">
      {faqItems.map((item, index) => {
        const isOpen = openIndex === index
        const panelId = `${baseId}-faq-panel-${index}`
        const headerId = `${baseId}-faq-header-${index}`

        return (
          <div key={item.question} className="border-b border-[var(--border)]">
            <button
              type="button"
              id={headerId}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpenIndex((current) => (current === index ? -1 : index))}
              className="flex w-full items-center gap-6 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
            >
              <span className="min-w-0 flex-1 font-['Manrope'] text-[16px] font-normal leading-5 tracking-normal text-[var(--foreground)] [text-wrap:pretty]">
                {item.question}
              </span>
              <span className="text-[22px] leading-none text-[var(--foreground)]" aria-hidden>
                {isOpen ? '×' : '+'}
              </span>
            </button>
            <div
              className={`grid ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'} transition-[grid-template-rows] duration-200 ease-out`}
            >
              <div className="min-h-0 overflow-hidden">
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={headerId}
                  aria-hidden={!isOpen}
                  className="pb-5 text-[15px] leading-[22.5px] tracking-normal text-[var(--foreground-muted)] [text-wrap:pretty]"
                >
                  {item.answer}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export const NetworkUpdatePage = () => {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle')
  const resetTimerRef = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current)
      }
    },
    [],
  )

  const handleCopyIntro = async () => {
    const ok = await copyToClipboard(introCopy)
    setCopyStatus(ok ? 'copied' : 'failed')

    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current)
    }
    resetTimerRef.current = window.setTimeout(() => {
      setCopyStatus('idle')
      resetTimerRef.current = null
    }, 1800)
  }

  const copyLabel =
    copyStatus === 'copied' ? 'Скопировано' : copyStatus === 'failed' ? 'Не удалось скопировать' : 'Скопировать интро'
  const copied = copyStatus === 'copied'
  const allCaseStudies = getAllCaseStudies()
  const allNotes = getAllNotes()
  const readAlsoCaseStudies = readAlsoSlugs
    .map((slug) => allCaseStudies.find((caseStudy) => caseStudy.page.slug === slug))
    .filter((caseStudy): caseStudy is CaseStudy => Boolean(caseStudy))
  const readAlsoNotes = readAlsoNoteSlugs
    .map((slug) => allNotes.find((note) => note.slug === slug))
    .filter((note): note is Note => Boolean(note))

  return (
    <>
      <SeoHead pathname={pathname} kind="private-follow-up" title="Custom Web — персональный follow-up" description="Персональные страницы для продолжения B2B-разговоров: контекст встречи, предложение и следующий шаг." />
      <div className="site-page mx-auto w-full min-w-0 max-w-[930px] px-4 pb-32 font-['Manrope'] md:px-0 md:pt-28">
        <header className="relative flex min-h-[430px] w-full items-start justify-center overflow-hidden rounded-[16px] bg-black px-3 py-[50px] text-white md:min-h-[453px] md:px-2">
          <video
            className="pointer-events-none absolute inset-0 size-full translate-y-[80px] object-cover object-[center_72%] md:translate-y-[112px] md:object-center"
            src={heroVideoUrl}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black via-black/50 via-40% to-transparent to-70%" />
          <div className="relative z-10 flex w-full max-w-[914px] flex-col items-center gap-[42px]">
            <div className="flex w-full flex-col items-center text-center">
              <p className="border-b border-white/40 px-2.5 py-1.5 text-[13px] font-medium leading-5 text-white">
                Что я сейчас делаю
              </p>
              <h1 className="ui-heading-balance mt-1 font-['Manrope'] text-[36px] font-normal leading-[1.02] tracking-normal text-white sm:text-[44px] sm:leading-[0.98] md:mt-0 md:text-[52px]">
                Гипер<wbr className="sm:hidden" />персональный<br className="hidden sm:block" /> follow-up для<br className="hidden sm:block" /> длинных B2B-сделок.
              </h1>
            </div>
            <div className="flex w-full flex-col items-center justify-center gap-2 min-[480px]:flex-row">
              <a
                href="#examples"
                className="inline-flex h-10 w-full max-w-[260px] items-center justify-center rounded-[8px] bg-black px-4 text-[14px] font-medium text-white transition-[background-color,transform] duration-150 hover:bg-[#202020] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black min-[480px]:w-auto"
              >
                Посмотреть примеры
              </a>
              <a
                href="#intro"
                className="inline-flex h-10 w-full max-w-[260px] items-center justify-center rounded-[8px] border border-[#e0e0e0] bg-white px-4 text-[14px] font-medium text-black transition-[background-color,transform] duration-150 hover:bg-[#f4f4f4] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black min-[480px]:w-auto"
              >
                Посмотреть интро
                <ArrowDown className="ml-1 size-[17px]" weight="regular" aria-hidden />
              </a>
            </div>
          </div>
        </header>

        <div className="mt-9 grid w-full grid-cols-1 gap-x-6 gap-y-6 lg:mx-auto lg:w-fit lg:grid-cols-[minmax(0,605px)_minmax(0,240px)]">
          <main className="min-w-0 lg:col-start-1 lg:row-start-1">
            <div className="space-y-16">
              <figure className="border-l-2 border-[var(--border-strong)] pl-4">
                <blockquote className="text-[17px] font-normal leading-7 tracking-normal text-[var(--foreground)] [text-wrap:pretty]">
                  “В длинных B2B-сделках дорого не просто получить внимание клиента, а удержать его после разговора. Ему не нужно еще одно письмо или PDF. Ему нужно увидеть, что его ситуацию поняли и следующий шаг собран вокруг того, что важно именно ему.”
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-10 w-8 shrink-0 items-center justify-center rounded-[6px] border border-[var(--border)] bg-[#fafafa] text-[12px] font-semibold text-[var(--foreground-muted)]">
                    ИФ
                  </span>
                  <span className="min-w-0">
                    <span className="block font-['Manrope'] text-[16px] leading-5 tracking-normal text-[var(--foreground)]">Имя Фамилия</span>
                    <span className="block text-[14px] leading-[19px] text-[var(--foreground-muted)]">роль / компания</span>
                  </span>
                </figcaption>
              </figure>

              <section id="why" className="space-y-4 scroll-mt-24">
                <SectionHeader eyebrow="Зачем" title="Чтобы клиент чувствовал: это собрано именно под него." />
                <p className="max-w-[56ch] text-[15px] leading-6 tracking-normal text-[var(--foreground-muted)] [text-wrap:pretty]">
                  В длинной B2B-продаже обычного письма часто не хватает. Нужно сохранить контекст разговора, показать релевантные примеры, объяснить следующий шаг и дать champion материал, который легко переслать дальше.
                </p>
                <div className="grid gap-2 md:grid-cols-3">
                  {whyCards.map(({ title, body, Icon }) => (
                    <article key={title} className={`${cardClass} min-h-[220px] md:min-h-[270px]`}>
                      <Icon aria-hidden className="mb-4 size-7 text-[var(--foreground-subtle)]" weight="regular" />
                      <h3 className="font-['Manrope'] text-[18px] font-normal leading-6 tracking-normal text-[var(--foreground)]">{title}</h3>
                      <p className="mt-3 text-[14px] leading-[22px] text-[var(--foreground-muted)] [text-wrap:pretty]">{body}</p>
                    </article>
                  ))}
                </div>
              </section>

              <section id="outputs" className="space-y-4 scroll-mt-24">
                <SectionHeader eyebrow="Что получается" title="Не один формат. Несколько простых вариантов." />
                <p className="max-w-[56ch] text-[15px] leading-6 tracking-normal text-[var(--foreground-muted)] [text-wrap:pretty]">
                  Формат зависит от того, что человеку нужно сделать после разговора: вспомнить детали, показать идею внутри своей команды, сравнить варианты или вернуться с понятным следующим шагом.
                </p>
                <div className="grid gap-2 md:grid-cols-2">
                  {outputs.map(({ title, body, Icon }) => (
                    <article key={title} className={`${cardClass} min-h-[188px] md:min-h-[204px]`}>
                      <Icon aria-hidden className="mb-4 size-7 text-[var(--foreground-subtle)]" weight="regular" />
                      <h3 className="font-['Manrope'] text-[20px] font-normal leading-6 tracking-normal text-[var(--foreground)]">{title}</h3>
                      <p className="mt-3 text-[14px] leading-[22px] text-[var(--foreground-muted)] [text-wrap:pretty]">{body}</p>
                    </article>
                  ))}
                </div>
              </section>

              <section id="examples" className="space-y-4 scroll-mt-24">
                <SectionHeader eyebrow="Примеры" title="Несколько живых примеров формата." />
                <p className="max-w-[56ch] text-[15px] leading-6 tracking-normal text-[var(--foreground-muted)] [text-wrap:pretty]">
                  Они не показывают “идеальный шаблон”. Они показывают тип мышления: взять конкретную ситуацию и собрать вокруг нее понятную страницу.
                </p>
                <div className="grid gap-2 md:grid-cols-2">
                  {examples.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Открыть пример: ${item.label}`}
                      className="group relative flex h-[240px] min-w-0 flex-col overflow-hidden rounded-[10px] border border-[var(--border)] bg-black p-[17px] text-white transition-[transform,box-shadow] duration-200 hover:shadow-[0_18px_34px_rgba(0,0,0,0.08)] active:scale-[0.995] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
                    >
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[58%] overflow-hidden">
                        <video
                          src={item.video}
                          poster={item.poster}
                          className="size-full object-cover"
                          autoPlay
                          muted
                          loop
                          playsInline
                          preload="metadata"
                          aria-hidden="true"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/55 to-transparent" />
                      </div>
                      <div className="relative z-10 flex min-h-0 flex-1 flex-col gap-[11px]">
                        <p className="text-[11px] font-semibold uppercase leading-4 tracking-[0.14em] text-[#767676]">{item.tag}</p>
                        <h3 className="max-w-[230px] font-['Manrope'] text-[24px] font-bold leading-6 tracking-normal text-white [text-wrap:balance]">{item.title}</h3>
                      </div>
                      <div className="relative z-10 flex min-w-0 items-center gap-[11px]">
                        <span
                          className={`flex size-6 shrink-0 items-center justify-center rounded-[3px] ${item.logoInset ? 'bg-white p-1' : 'overflow-hidden'}`}
                        >
                          <img
                            src={item.logo}
                            alt=""
                            width={24}
                            height={24}
                            className={item.logoInset ? 'size-4 object-contain' : 'size-full object-cover'}
                          />
                        </span>
                        <span className="min-w-0 truncate text-[16px] font-normal uppercase leading-6 tracking-normal text-white">{item.label}</span>
                        <ArrowUpRight className="ml-auto size-4 shrink-0 text-white/60 transition-transform duration-150 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden />
                      </div>
                    </a>
                  ))}
                </div>
              </section>

              <section id="intro" className="space-y-4 scroll-mt-24">
                <SectionHeader eyebrow="Интро" title="Если кто-то пришел в голову, можно просто познакомить нас." />
                <p className="text-[15px] leading-6 tracking-normal text-[var(--foreground-muted)]">Короткий текст ниже можно переслать в личке.</p>
                <button
                  type="button"
                  onClick={handleCopyIntro}
                  className="group block w-full border-l-2 border-[var(--border-strong)] px-[18px] py-[3px] text-left text-[14px] font-semibold leading-6 tracking-normal text-[var(--foreground)] decoration-[var(--border-strong)] underline-offset-4 [transition-property:border-color,text-decoration-color] duration-150 hover:border-[var(--foreground)] hover:decoration-[var(--foreground)] focus-visible:rounded-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
                  aria-label={copyLabel}
                  title={copyLabel}
                >
                  <span className="whitespace-pre-wrap underline decoration-inherit [text-underline-position:from-font]">{introCopy}</span>
                  <CopyIcon copied={copied} />
                </button>
              </section>

              <section id="faq" className="space-y-4 scroll-mt-24">
                <SectionHeader eyebrow="FAQ" title="Коротко про механику, письма и сайты." />
                <FaqList />
              </section>

              <div className="-mx-4">
                <LazyMotion features={loadDomAnimation} strict>
                  <Suspense fallback={null}>
                    <HomeTestimonials />
                  </Suspense>
                </LazyMotion>
              </div>

              <HomeAiCaseStudies
                title="Read also"
                items={readAlsoCaseStudies}
                notes={readAlsoNotes}
                showExploreMore={false}
                padded={false}
                variant="readAlso"
                stackOnMobile
              />
            </div>
          </main>

          <aside
            className={
              'hidden w-full min-w-0 max-w-[240px] shrink flex-col lg:sticky lg:top-0 lg:col-start-2 lg:row-start-1 lg:flex lg:h-[100dvh] lg:self-start lg:overflow-hidden lg:pb-4'
            }
          >
            <div className="min-h-0 pt-4 lg:flex-1 lg:overflow-y-auto lg:overscroll-y-contain [scrollbar-width:thin]">
              <CaseStudyContentSheet items={contentSheetItems} linkClassName="font-['Manrope']" />
            </div>
            <div className="hidden shrink-0 lg:block">
              <CaseStudyBookCallCta variant="inline" />
            </div>
          </aside>
        </div>
      </div>
      <CaseStudyBookCallCta variant="fixed" />
    </>
  )
}
